// Deterministic gradient avatars + a small overlapping stack, used by the board
// cards and the project hero meta row. Inline SVG / CSS only — no external assets.

const GRADIENTS = [
  "linear-gradient(135deg,#10b981,#3b82f6)",
  "linear-gradient(135deg,#2dd4bf,#3b82f6)",
  "linear-gradient(135deg,#3b82f6,#2dd4bf)",
  "linear-gradient(135deg,#f5b252,#ff6b6b)",
  "linear-gradient(135deg,#34d399,#0f766e)",
  "linear-gradient(135deg,#2f6fe4,#7c5cff)",
];

export function initials(name?: string | null): string {
  if (!name) return "?";
  return (
    name
      .trim()
      .split(/\s+/)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .slice(0, 2)
      .join("") || "?"
  );
}

/** Stable index from a name so the same person keeps the same colour. */
function hashIndex(seed: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h % mod;
}

export function Avatar({
  name,
  size = "md",
  title,
}: {
  name?: string | null | undefined;
  size?: "sm" | "md";
  title?: string | undefined;
}) {
  if (!name) {
    return (
      <span className={`av ${size} none`} title={title ?? "Unassigned"} aria-hidden="true">
        ?
      </span>
    );
  }
  const bg = GRADIENTS[hashIndex(name, GRADIENTS.length)];
  return (
    <span
      className={`av ${size}`}
      style={{ background: bg }}
      title={title ?? name}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

/** Overlapping avatar stack with a "+N" overflow bubble. */
export function AvatarStack({
  names,
  max = 4,
  size = "md",
}: {
  names: string[];
  max?: number;
  size?: "sm" | "md";
}) {
  const shown = names.slice(0, max);
  const extra = names.length - shown.length;
  return (
    <span className="av-stack">
      {shown.map((n, i) => (
        <Avatar key={`${n}-${i}`} name={n} size={size} />
      ))}
      {extra > 0 ? (
        <span className={`av ${size} more`} title={`${extra} more`} aria-hidden="true">
          +{extra}
        </span>
      ) : null}
    </span>
  );
}
